import React, { Component } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import Select from 'react-select';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import Breadcrumbs from './Breadcrumb';
import authAxios from './api';
import {
  AcademicYearOptions,
  defaulterrmsg,
  GetOrgId,
  GetSecurityId,
  getPaymentType,
} from './Functions';
import {
  classifyCourseType,
  COURSE_TYPE_OPTIONS,
  formatMoney,
} from './feeStructure';
import SemanticReviewPanel from './SemanticReviewPanel';

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
};

const historicalFeeEndpoint = (path) => {
  const configured = String(process.env.REACT_APP_HISTORICAL_FEE_API || process.env.REACT_APP_SEMANTIC_REVIEW_API || '').replace(/\/+$/, '');
  if (configured) return `${configured}/legacy-fee-mapping/${path}`;
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return `http://localhost:3012/legacy-fee-mapping/${path}`;
  }
  return `legacy-fee-mapping/${path}`;
};

class HistoricalFeeMapping extends Component {
  constructor(props) {
    super(props);
    this.state = {
      breadcrumbItems: [{ title: 'Fee', link: '#' }, { title: 'Historical Fee Mapping', link: '#' }],
      org_id: GetOrgId(),
      operator_id: GetSecurityId(),
      branchOptions: [],
      calendarOptions: [],
      academicOptions: [],
      selectedCourseType: null,
      selectedBranch: null,
      selectedCalendar: null,
      selectedAcademic: null,
      inventory: null,
      editors: {},
      loadingOptions: true,
      loadingAcademic: false,
      loadingInventory: false,
      workingKey: '',
      search: '',
      activeFeeId: '',
      activeBillNumber: '',
      billDetail: null,
      billDetailError: '',
      loadingBillDetail: false,
      showCompleted: false,
      workspace: 'mapping',
    };
    toastr.options = { closeButton: true, progressBar: true };
  }

  async componentDidMount() {
    try {
      const branchResponse = await authAxios.post(historicalFeeEndpoint('branches'), { org_id: this.state.org_id, security_id: this.state.operator_id });
      const rawBranches = (branchResponse.data || []).map((branch) => ({
        value: branch._id,
        label: branch.branch_name,
        baseLabel: branch.branch_name,
        branch_name: branch.branch_name,
        academic_year: branch.academic_years_value,
        year_labels: branch.year_labels || [],
        fee_model: branch.fee_model || 'yearly',
        going_forward: !!branch.going_forward,
        course_type: branch.course_type || '',
        branch_group: branch.branch_group,
      }));
      const nameCounts = rawBranches.reduce((counts, branch) => {
        const name = String(branch.baseLabel || branch.label || '').trim().toUpperCase();
        counts[name] = (counts[name] || 0) + 1;
        return counts;
      }, {});
      const branchOptions = rawBranches.map((branch) => {
        const baseLabel = branch.baseLabel || branch.label;
        const duplicate = nameCounts[String(baseLabel || '').trim().toUpperCase()] > 1;
        return {
          ...branch,
          baseLabel,
          label: `${baseLabel}${branch.branch_group === 'historical_only' ? ' — Historical branch' : (duplicate ? ` — ${branch.going_forward ? 'Current intake' : 'Existing batches'}` : '')}`,
          course_type: classifyCourseType(branch),
        };
      }).sort((a, b) => String(a.label).localeCompare(String(b.label)));
      this.setState({ branchOptions, loadingOptions: false });
    } catch (error) {
      this.setState({ loadingOptions: false });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  }

  selectMenuProps = () => ({
    menuPortalTarget: typeof document === 'undefined' ? null : document.body,
    menuPosition: 'fixed',
    styles: selectMenuStyles,
  });

  getCourseBranches = () => {
    const courseType = this.state.selectedCourseType?.value;
    return courseType
      ? this.state.branchOptions.filter((branch) => branch.course_type === courseType)
      : [];
  };

  getCompatibleSessions = () => {
    // The server returns only cohorts evidenced for the exact branch whose
    // duration matches its approved Degree/P.G./Inter/School structure.
    return this.state.calendarOptions;
  };

  getYearLabel = (option) => {
    const order = Number(option?.order || 1);
    return this.state.selectedBranch?.year_labels?.[order - 1]
      || option?.baseLabel
      || option?.label
      || `Year ${order}`;
  };

  handleCourseType = (selectedCourseType) => this.setState({
    selectedCourseType,
    selectedBranch: null,
    selectedCalendar: null,
    selectedAcademic: null,
    calendarOptions: [],
    academicOptions: [],
    inventory: null,
    editors: {},
    activeFeeId: '',
    showCompleted: false,
    loadingAcademic: false,
  });

  handleBranch = async (selectedBranch) => {
    this.setState({
      selectedBranch,
      selectedCalendar: null,
      selectedAcademic: null,
      calendarOptions: [],
      academicOptions: [],
      inventory: null,
      editors: {},
      activeFeeId: '',
      showCompleted: false,
      loadingAcademic: !!selectedBranch,
    });
    if (!selectedBranch) return;
    try {
      const [options, cohortResponse] = await Promise.all([
        AcademicYearOptions(this.state.org_id, selectedBranch.academic_year),
        authAxios.post(historicalFeeEndpoint('cohorts'), {
          org_id: this.state.org_id,
          security_id: this.state.operator_id,
          branch_id: selectedBranch.value,
        }),
      ]);
      if (this.state.selectedBranch?.value !== selectedBranch.value) return;
      const academicOptions = options.map((option) => ({
        ...option,
        baseLabel: selectedBranch.year_labels?.[Number(option.order || 1) - 1] || option.label,
      }));
      const calendarOptions = (cohortResponse.data || []).map((cohort) => ({
        value: cohort._id,
        label: cohort.calendar_year,
        calendar_year_value: cohort.calendar_year_value,
        current_active: cohort.current_active,
        duration: cohort.duration,
      }));
      this.setState({ academicOptions, calendarOptions, loadingAcademic: false });
    } catch (error) {
      this.setState({ academicOptions: [], calendarOptions: [], loadingAcademic: false });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  handleCalendar = (selectedCalendar) => {
    const oneYear = Number(this.state.selectedBranch?.academic_year) === 1;
    const selectedAcademic = oneYear ? (this.state.academicOptions[0] || null) : null;
    this.setState({ selectedCalendar, selectedAcademic, inventory: null, editors: {}, activeFeeId: '', showCompleted: false }, () => {
      if (selectedCalendar && selectedAcademic) this.loadInventory();
    });
  };

  handleAcademic = (selectedAcademic) => this.setState({ selectedAcademic, inventory: null, editors: {}, activeFeeId: '', showCompleted: false }, () => {
    if (selectedAcademic) this.loadInventory();
  });

  selectionPayload = () => ({
    org_id: this.state.org_id,
    security_id: this.state.operator_id,
    branch_id: this.state.selectedBranch?.value,
    course_type: this.state.selectedCourseType?.value,
    calendar_years_id: this.state.selectedCalendar?.value,
    academic_years_id: this.state.selectedAcademic?.value,
  });

  feeKey = (fee) => fee.evidence_key || `${fee.source_academic_years_id || ''}:${fee.legacy_sub_fee_id}`;

  editorsFromInventory = (inventory) => Object.fromEntries((inventory.legacy_fees || []).map((fee) => {
    const mapping = fee.mapping;
    return [this.feeKey(fee), {
      mapping_decision: mapping?.mapping_decision || 'mapped',
      target_canonical_code: mapping?.target_canonical_code || '',
      target_scope: mapping?.mapping_decision === 'mapped' ? (mapping?.target_scope || 'all_slots_equal') : '',
      target_installment_key: mapping?.target_installment_key || '',
      review_notes: mapping?.review_notes || '',
      preview: null,
    }];
  }));

  loadInventory = async () => {
    if (!this.state.selectedBranch || !this.state.selectedCalendar || !this.state.selectedAcademic) return;
    this.setState({ loadingInventory: true, workingKey: '' });
    try {
      const { data } = await authAxios.post(historicalFeeEndpoint('inventory'), this.selectionPayload());
      this.setState({ inventory: data, editors: this.editorsFromInventory(data), loadingInventory: false, workingKey: '' });
    } catch (error) {
      this.setState({ inventory: null, editors: {}, loadingInventory: false, workingKey: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  updateEditor = (legacyId, patch) => this.setState((state) => ({
    editors: {
      ...state.editors,
      [legacyId]: { ...state.editors[legacyId], ...patch, preview: patch.preview === undefined ? null : patch.preview },
    },
  }));

  mappingPayload = (fee) => {
    const editor = this.state.editors[this.feeKey(fee)] || {};
    return {
      ...this.selectionPayload(),
      legacy_sub_fee_id: fee.legacy_sub_fee_id,
      source_academic_years_id: fee.source_academic_years_id,
      mapping_decision: editor.mapping_decision,
      target_canonical_code: editor.mapping_decision === 'mapped' ? editor.target_canonical_code : '',
      target_scope: editor.mapping_decision === 'mapped' ? (editor.target_scope || 'all_slots_equal') : '',
      target_installment_key: editor.mapping_decision === 'mapped' && editor.target_scope === 'single_slot'
        ? editor.target_installment_key
        : '',
      review_notes: editor.review_notes || '',
      preview_stack_signature: editor.preview?.stack_preview?.signature || '',
      created_by: this.state.operator_id,
      updated_by: this.state.operator_id,
    };
  };

  previewMapping = async (fee) => {
    const feeKey = this.feeKey(fee);
    const editor = this.state.editors[feeKey] || {};
    if (editor.mapping_decision === 'mapped' && !editor.target_canonical_code) return toastr.error('Choose a canonical target first.');
    const key = `preview:${feeKey}`;
    this.setState({ workingKey: key });
    try {
      const { data } = await authAxios.post(historicalFeeEndpoint('preview'), this.mappingPayload(fee));
      this.updateEditor(feeKey, { preview: data });
      this.setState({ workingKey: '' });
    } catch (error) {
      this.setState({ workingKey: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  saveDraft = async (fee) => {
    const feeKey = this.feeKey(fee);
    const editor = this.state.editors[feeKey] || {};
    if (editor.mapping_decision === 'mapped' && !editor.target_canonical_code) return toastr.error('Choose a canonical target first.');
    if (!editor.preview) return toastr.error('Preview the combined result before saving this draft.');
    const key = `draft:${feeKey}`;
    this.setState({ workingKey: key });
    try {
      await authAxios.post(historicalFeeEndpoint('draft'), this.mappingPayload(fee));
      toastr.success('Mapping draft saved.');
      await this.loadInventory();
    } catch (error) {
      this.setState({ workingKey: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  runLifecycle = async (fee, action) => {
    if (!fee.mapping?.mapping_id) return;
    const confirmation = action === 'approve'
      ? 'Approve this mapping and its current combined stack? The source evidence and every fee sharing the target will be checked again.'
      : action === 'reopen'
        ? 'Reopen this approved mapping as a draft so it can be reviewed and saved again?'
        : 'Save this mapping permanently? A protected copy will be stored outside the application database for future migrations. This decision cannot be edited afterward; original fees, payments, bills, and receipts will remain unchanged.';
    if (!window.confirm(confirmation)) return;
    const key = `${action}:${this.feeKey(fee)}`;
    this.setState({ workingKey: key });
    try {
      await authAxios.post(historicalFeeEndpoint(action), {
        org_id: this.state.org_id,
        security_id: this.state.operator_id,
        mapping_id: fee.mapping.mapping_id,
        updated_by: this.state.operator_id,
      });
      toastr.success(action === 'approve'
        ? 'Mapping approved.'
        : action === 'reopen'
          ? 'Mapping reopened as a draft.'
          : 'Mapping permanently saved in the protected archive.');
      await this.loadInventory();
    } catch (error) {
      this.setState({ workingKey: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  renderPreview = (preview) => {
    if (!preview) return null;
    if (preview.mapping_decision === 'historical_only') {
      return <Alert color="secondary" className="mt-3 mb-0">This fee will stay as an old record and will not be combined with a new fee name.</Alert>;
    }
    const students = preview.affected_student_preview || [];
    const installmentHeaders = students[0]?.target_installments || [];
    return (
      <div className="mt-3">
        <div className="fw-bold mb-1">Preview: how this old fee will appear</div>
        <div className="small text-muted mb-2">Check the new fee part, every affected student, collected amount, and remaining due before saving.</div>
        <Table responsive bordered size="sm" className="mb-2 bg-white">
          <thead><tr><th>New fee part</th><th className="text-end">Branch fee amount</th><th className="text-end">Student fee total</th><th className="text-end">Collected</th><th className="text-end">Due</th></tr></thead>
          <tbody>
            {(preview.target_installments || []).map((slot) => (
              <tr key={slot.installment_key}>
                <td>{slot.installment_label}</td>
                <td className="text-end">{slot.schedule_amount === null || slot.schedule_amount === undefined ? 'Not available' : formatMoney(slot.schedule_amount)}</td>
                <td className="text-end">{formatMoney(slot.student_actual_amount)}</td>
                <td className="text-end text-success">{formatMoney(slot.student_collected_amount)}</td>
                <td className="text-end text-warning">{formatMoney(slot.student_due_amount)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="small text-muted"><i className="mdi mdi-shield-check-outline me-1" />Existing payments and receipts stay exactly as they are.</div>
        {this.renderStackPreview(preview.stack_preview)}
        {students.length > 0 && (
          <details className="mt-3">
            <summary className="fw-bold text-primary" style={{ cursor: 'pointer' }}>
              Show student-by-student check ({students.length})
            </summary>
            <div className="table-responsive mt-2" style={{ maxHeight: '420px' }}>
              <Table bordered hover size="sm" className="mb-0 bg-white">
                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Student ID</th>
                    <th>Name</th>
                    <th className="text-end">Old fee</th>
                    <th className="text-end">Collected</th>
                    <th className="text-end">Due</th>
                    {installmentHeaders.map((slot) => <th key={slot.installment_key} className="text-end">{slot.installment_label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.student_id}>
                      <td><code>{student.student_display_id || student.student_id}</code></td>
                      <td>{student.student_name || '—'}</td>
                      <td className="text-end">{formatMoney(student.source_actual_amount)}</td>
                      <td className="text-end">{formatMoney(Math.max(Number(student.source_paid_amount || 0), Number(student.source_collected_amount || 0)))}</td>
                      <td className="text-end">{formatMoney(student.source_due_amount)}</td>
                      {(student.target_installments || []).map((slot) => <td key={slot.installment_key} className="text-end">{formatMoney(Math.max(Number(slot.amount || 0), Number(slot.collected_amount || 0)))}</td>)}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {(preview.target_installments || []).length > 1 && (
              <div className="small text-muted mt-2">If an amount cannot be divided evenly, the small balance is added to that student’s final part so the total stays correct.</div>
            )}
          </details>
        )}
      </div>
    );
  };

  renderStackPreview = (stackPreview) => {
    const groups = stackPreview?.groups || [];
    if (!groups.length) return null;
    return (
      <div className="border rounded bg-light p-3 mt-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <div>
            <div className="fw-bold">Combined stack after this mapping</div>
            <div className="small text-muted">Every old source remains separate in the database. These totals show their unified new fee position.</div>
          </div>
          <Badge color={groups.some((group) => group.source_count > 1) ? 'primary' : 'secondary'} pill>
            {groups.some((group) => group.source_count > 1) ? 'Stacked target' : 'First fee on target'}
          </Badge>
        </div>
        <Table responsive bordered size="sm" className="mb-2 bg-white">
          <thead><tr><th>New fee part</th><th>Old fees combined</th><th className="text-end">Students</th><th className="text-end">Combined fee</th><th className="text-end">Collected</th><th className="text-end">Due</th></tr></thead>
          <tbody>{groups.map((group) => (
            <tr key={group.stack_key}>
              <td><strong>{group.canonical_code}</strong> — {group.installment_label}</td>
              <td>{(group.legacy_sources || []).map((source) => (
                <Badge key={source.evidence_key} color={source.is_current_source ? 'primary' : 'light'} className={source.is_current_source ? 'me-1' : 'text-dark border me-1'}>
                  {source.legacy_fee_name}{source.is_current_source ? ' (this fee)' : ''}
                </Badge>
              ))}</td>
              <td className="text-end">{group.students_affected || 0}</td>
              <td className="text-end">{formatMoney(group.student_actual_amount)}</td>
              <td className="text-end text-success">{formatMoney(group.student_collected_amount)}</td>
              <td className="text-end text-warning">{formatMoney(group.student_due_amount)}</td>
            </tr>
          ))}</tbody>
        </Table>
        <details className="mt-2">
          <summary className="fw-bold text-primary" style={{ cursor: 'pointer' }}>Show combined student-by-student stack</summary>
          {groups.map((group) => (
            <div className="mt-3" key={`students:${group.stack_key}`}>
              <div className="fw-bold mb-1">{group.canonical_code} — {group.installment_label}</div>
              <div className="table-responsive" style={{ maxHeight: '360px' }}>
                <Table bordered hover size="sm" className="mb-0 bg-white">
                  <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr><th>Student ID</th><th>Name</th><th>Legacy fees included</th><th className="text-end">Combined fee</th><th className="text-end">Collected</th><th className="text-end">Due</th></tr></thead>
                  <tbody>{(group.student_rows || []).map((student) => (
                    <tr key={`${group.stack_key}:${student.student_id || student.student_display_id}`}>
                      <td><code>{student.student_display_id || student.student_id}</code></td>
                      <td>{student.student_name || '—'}</td>
                      <td>{(student.legacy_sources || []).map((source) => source.legacy_fee_name).join(' + ')}</td>
                      <td className="text-end">{formatMoney(student.actual_amount)}</td>
                      <td className="text-end text-success">{formatMoney(student.collected_amount)}</td>
                      <td className="text-end text-warning">{formatMoney(student.due_amount)}</td>
                    </tr>
                  ))}</tbody>
                </Table>
              </div>
            </div>
          ))}
        </details>
      </div>
    );
  };

  stackedSources = (canonicalCode, installmentKeys, excludeEvidenceKey = '') => {
    const keys = new Set(installmentKeys || []);
    const sources = new Map();
    (this.state.inventory?.target_stacks || [])
      .filter((group) => group.canonical_code === canonicalCode && keys.has(group.installment_key))
      .flatMap((group) => group.legacy_sources || [])
      .filter((source) => source.evidence_key !== excludeEvidenceKey)
      .forEach((source) => sources.set(source.evidence_key, source));
    return [...sources.values()];
  };

  targetOptions = (fee = null) => {
    const excluded = fee ? this.feeKey(fee) : '';
    const mappedOptions = (this.state.inventory?.canonical_heads || []).reduce((options, head) => {
      const setFeeControlled = ['TERM', 'STAT'].includes(head.code);
      const existingTarget = fee?.mapping?.target_canonical_code === head.code;
      if (setFeeControlled && !existingTarget) return options;
      const slots = head.slots || [];
      const wholeStackCount = this.stackedSources(head.code, slots.map((slot) => slot.key), excluded).length;
      const wholeStackSuffix = wholeStackCount ? ` · combines with ${wholeStackCount} other old fee${wholeStackCount === 1 ? '' : 's'}` : '';
      options.push({
        value: `all:${head.code}`,
        label: `${slots.length > 1
          ? `${head.label} — divide equally into ${slots.map((slot) => slot.label).join(' + ')}`
          : `${head.label} — ${slots[0]?.kind === 'one_time' ? 'one-time fee' : (slots[0]?.label || head.label)}`}${wholeStackSuffix}`,
        mapping_decision: 'mapped',
        target_canonical_code: head.code,
        target_scope: 'all_slots_equal',
        target_installment_key: '',
      });
      if (slots.length > 1) {
        slots.forEach((slot) => {
          const stackCount = this.stackedSources(head.code, [slot.key], excluded).length;
          options.push({
            value: `slot:${head.code}:${slot.key}`,
            label: `${head.label} — ${slot.label} only${stackCount ? ` · combines with ${stackCount} other old fee${stackCount === 1 ? '' : 's'}` : ''}`,
            mapping_decision: 'mapped',
            target_canonical_code: head.code,
            target_scope: 'single_slot',
            target_installment_key: slot.key,
          });
        });
      }
      return options;
    }, []);
    return [
      ...mappedOptions,
      {
        value: '__historical_only',
        label: 'Keep as an old record only — do not combine',
        mapping_decision: 'historical_only',
        target_canonical_code: '',
        target_scope: '',
        target_installment_key: '',
      },
    ];
  };

  mappingChoice = (editor, fee = null) => {
    if (editor.mapping_decision === 'historical_only') {
      return this.targetOptions(fee).find((option) => option.mapping_decision === 'historical_only') || null;
    }
    const scope = editor.target_scope || 'all_slots_equal';
    return this.targetOptions(fee).find((option) => (
      option.mapping_decision === 'mapped'
      && option.target_canonical_code === editor.target_canonical_code
      && option.target_scope === scope
      && (scope !== 'single_slot' || option.target_installment_key === editor.target_installment_key)
    )) || null;
  };

  setMappingChoice = (fee, option) => {
    this.updateEditor(this.feeKey(fee), {
      mapping_decision: option?.mapping_decision || 'mapped',
      target_canonical_code: option?.target_canonical_code || '',
      target_scope: option?.target_scope || '',
      target_installment_key: option?.target_installment_key || '',
    });
  };

  friendlyStatus = (mapping) => {
    if (!mapping) return { label: 'Needs review', color: 'warning', className: 'text-dark' };
    if (mapping.rule_locked) return { label: 'Set Fee rule', color: 'success', className: '' };
    if (mapping.mapping_status === 'draft') return { label: 'Draft saved', color: 'primary', className: '' };
    if (mapping.mapping_status === 'approved') return { label: 'Approved', color: 'info', className: 'text-dark' };
    return { label: 'Saved permanently', color: 'success', className: '' };
  };

  mappingResult = (fee) => {
    const editor = this.state.editors[this.feeKey(fee)] || {};
    if (editor.mapping_decision === 'historical_only') return 'Old record only';
    const target = this.mappingChoice(editor, fee);
    return target ? target.label : 'Not selected';
  };

  closeReviewModal = () => this.setState({
    activeFeeId: '',
    activeBillNumber: '',
    billDetail: null,
    billDetailError: '',
    loadingBillDetail: false,
  });

  closeBillDetails = () => this.setState({
    activeBillNumber: '',
    billDetail: null,
    billDetailError: '',
    loadingBillDetail: false,
  });

  openBillDetails = async (bill) => {
    if (!bill?.transaction_document_id || !bill?.transaction_row_id) {
      toastr.error('This historical bill does not have a complete source reference.');
      return;
    }
    this.setState({
      activeBillNumber: bill.bill_number || 'Historical bill',
      billDetail: null,
      billDetailError: '',
      loadingBillDetail: true,
    });
    try {
      const { data } = await authAxios.post(historicalFeeEndpoint('bill-details'), {
        org_id: this.state.org_id,
        security_id: this.state.operator_id,
        transaction_document_id: bill.transaction_document_id,
        transaction_row_id: bill.transaction_row_id,
      });
      this.setState({ billDetail: data, loadingBillDetail: false });
    } catch (error) {
      this.setState({
        billDetailError: error?.response?.data?.message || 'Unable to load this bill.',
        loadingBillDetail: false,
      });
    }
  };

  renderBillDetailsModal = () => {
    const { activeBillNumber, billDetail, billDetailError, loadingBillDetail } = this.state;
    if (!activeBillNumber) return null;
    const activeLines = (billDetail?.lines || []).filter((line) => line.is_active);
    const inactiveLines = (billDetail?.lines || []).filter((line) => !line.is_active);
    const paymentMethods = (billDetail?.payment_methods || [])
      .map((method) => getPaymentType(method) || method)
      .filter(Boolean)
      .join(', ');
    return (
      <Modal isOpen toggle={this.closeBillDetails} size="lg" scrollable zIndex={1070} className="bill-detail-modal">
        <ModalHeader toggle={this.closeBillDetails}>Bill {billDetail?.bill_number || activeBillNumber}</ModalHeader>
        <ModalBody>
          {loadingBillDetail && <div className="text-center py-5"><Spinner color="primary" /><div className="mt-2 text-muted">Loading the complete bill…</div></div>}
          {billDetailError && <Alert color="danger" className="mb-0">{billDetailError}</Alert>}
          {billDetail && <>
            <div className="bill-receipt-heading mb-3">
              <div>
                <div className="review-kicker">Historical bill copy</div>
                <h4 className="mb-1">{billDetail.student?.student_name || 'Student name unavailable'}</h4>
                <div className="text-muted">Student ID: <strong>{billDetail.student?.student_display_id || 'Unavailable'}</strong></div>
              </div>
              <div className="text-md-end">
                <div><span className="text-muted">Bill number:</span> <strong>{billDetail.bill_number}</strong></div>
                <div><span className="text-muted">Bill date:</span> <strong>{billDetail.bill_date || 'Unavailable'}</strong></div>
                <div><span className="text-muted">Payment:</span> <strong>{paymentMethods || 'Unavailable'}</strong></div>
              </div>
            </div>

            <Alert color="info" className="border-0">
              These are the exact active legacy fee lines collected under this bill number. The total is reconstructed from the preserved transaction lines in the accepted migration snapshot.
            </Alert>

            <h6 className="mb-2">Collected fee types ({activeLines.length})</h6>
            <div className="table-responsive">
              <Table bordered size="sm" className="mb-0 bg-white align-middle">
                <thead className="table-light">
                  <tr><th>Fee type collected</th><th>Transaction</th><th>Method</th><th>Status</th><th className="text-end">Amount</th></tr>
                </thead>
                <tbody>
                  {activeLines.map((line) => (
                    <tr key={line.transaction_row_id} className={line.is_selected_line ? 'table-primary' : ''}>
                      <td>
                        <strong>{line.legacy_fee_name}</strong>
                        {line.legacy_parent_fee_name && line.legacy_parent_fee_name !== line.legacy_fee_name && <div className="small text-muted">Under {line.legacy_parent_fee_name}</div>}
                        {line.source_fee_id !== null && line.source_fee_id !== undefined && <div className="small text-muted">Old-system fee ID: {line.source_fee_id}</div>}
                      </td>
                      <td>{line.transaction_number || line.transaction_id || '—'}</td>
                      <td>{getPaymentType(line.payment_method) || line.payment_method || '—'}</td>
                      <td><Badge color="success">Collected</Badge></td>
                      <td className="text-end fw-bold">{formatMoney(line.amount)}</td>
                    </tr>
                  ))}
                  {!activeLines.length && <tr><td colSpan="5" className="text-center text-muted py-4">No active collected lines remain on this bill.</td></tr>}
                </tbody>
                <tfoot className="table-light"><tr><th colSpan="4" className="text-end">Bill collected total</th><th className="text-end">{formatMoney(billDetail.active_total)}</th></tr></tfoot>
              </Table>
            </div>

            {inactiveLines.length > 0 && (
              <details className="border rounded p-3 mt-3">
                <summary className="fw-bold text-muted" style={{ cursor: 'pointer' }}>Show reversed or inactive lines ({inactiveLines.length})</summary>
                <Table responsive bordered size="sm" className="mt-3 mb-0 bg-white align-middle">
                  <thead className="table-light"><tr><th>Fee type</th><th>Transaction</th><th>Status</th><th className="text-end">Amount</th></tr></thead>
                  <tbody>{inactiveLines.map((line) => (
                    <tr key={line.transaction_row_id}>
                      <td>{line.legacy_fee_name}</td>
                      <td>{line.transaction_number || line.transaction_id || '—'}</td>
                      <td><Badge color="secondary">Not collected</Badge></td>
                      <td className="text-end">{formatMoney(line.amount)}</td>
                    </tr>
                  ))}</tbody>
                </Table>
                <div className="small text-muted mt-2">These lines are preserved for audit but are excluded from the collected total.</div>
              </details>
            )}
          </>}
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={this.closeBillDetails}>Back to fee review</Button></ModalFooter>
      </Modal>
    );
  };

  renderFeeRow = (fee) => {
    const snapshot = fee.source_snapshot || {};
    const status = this.friendlyStatus(fee.mapping);
    return (
      <tr key={this.feeKey(fee)}>
        <td>
          <div className="fw-bold">{fee.legacy_fee_name}</div>
          <div className="small text-muted">{snapshot.students || 0} students · database stored it under {fee.source_academic_year || `Year ${fee.source_study_year || '?'}`}</div>
          {snapshot.first_payment_date && <div className="small text-muted">Collected {snapshot.first_payment_date}{snapshot.last_payment_date && snapshot.last_payment_date !== snapshot.first_payment_date ? ` to ${snapshot.last_payment_date}` : ''}</div>}
        </td>
        <td className="text-end">{formatMoney(snapshot.actual_amount)}</td>
        <td className="text-end">{formatMoney(Math.max(Number(snapshot.evidenced_paid_amount || 0), Number(snapshot.paid_amount || 0), Number(snapshot.collected_amount || 0)))}</td>
        <td className="text-end">{formatMoney(snapshot.due_amount)}</td>
        <td>{this.mappingResult(fee)}</td>
        <td><Badge color={status.color} className={status.className} pill>{status.label}</Badge></td>
        <td className="text-end">
          <Button color={fee.mapping?.mapping_status === 'applied' ? 'secondary' : 'primary'} outline size="sm" onClick={() => this.setState({ activeFeeId: this.feeKey(fee) })}>
            {fee.mapping?.rule_locked ? 'View system mapping' : (fee.mapping?.mapping_status === 'applied' ? 'View saved mapping' : (fee.mapping ? 'Open' : 'Review'))}
          </Button>
        </td>
      </tr>
    );
  };

  renderReviewModal = () => {
    const fee = (this.state.inventory?.legacy_fees || []).find((row) => this.feeKey(row) === this.state.activeFeeId);
    if (!fee) return null;
    const editor = this.state.editors[this.feeKey(fee)] || {};
    const mapping = fee.mapping;
    const locked = mapping && mapping.mapping_status !== 'draft';
    const busy = this.state.workingKey.endsWith(this.feeKey(fee));
    const snapshot = fee.source_snapshot || {};
    const billRows = fee.bill_rows || [];
    const billTotal = billRows.reduce((sum, bill) => sum + Number(bill.amount || 0), 0);
    const status = this.friendlyStatus(mapping);
    return (
      <>
      <Modal isOpen toggle={this.closeReviewModal} size="xl" scrollable>
        <ModalHeader toggle={this.closeReviewModal}>
          Review old fee: {fee.legacy_fee_name}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="text-muted">Assign this old evidence to <strong>{this.state.inventory?.selection?.target_year_label || this.state.selectedAcademic?.label}</strong>. Its migrated year tag will remain unchanged for audit history.</div>
            <Badge color={status.color} className={status.className} pill>{status.label}</Badge>
          </div>

          {mapping?.rule_locked ? (
            <Alert color="success" className="border-0">
              <i className="mdi mdi-shield-check-outline me-1" /><strong>Mapped automatically from PHP Set Fee.</strong> Exact legacy {mapping.target_canonical_code} is assigned to the same study year and divided equally into the canonical fee parts. The PHP Set Fee controls the branch amount; the totals below remain preserved legacy evidence.
            </Alert>
          ) : mapping?.mapping_status === 'applied' ? (
            <Alert color="success" className="border-0">
              <i className="mdi mdi-shield-lock-outline me-1" /><strong>Permanently saved.</strong> This mapping is view-only and has a protected copy outside the application database for recovery after future migrations.
              {mapping.archive_protected_at && <div className="small mt-1">Protected on {new Date(mapping.archive_protected_at).toLocaleString('en-IN')}.</div>}
            </Alert>
          ) : (
            <Alert color="info" className="border-0">
              <i className="mdi mdi-pencil-outline me-1" /><strong>Still editable.</strong> Save a draft, approve it, and use <strong>Save permanently</strong> only after the preview is correct. An approved mapping can still be reopened.
            </Alert>
          )}

          <Row className="g-2 mb-4">
            <Col md={3} sm={6}><div className="border rounded p-3 h-100"><div className="small text-muted">Students affected</div><h5 className="mb-0">{snapshot.students || 0}</h5></div></Col>
            <Col md={3} sm={6}><div className="border rounded p-3 h-100"><div className="small text-muted">Original fee total</div><h5 className="mb-0">{formatMoney(snapshot.actual_amount)}</h5></div></Col>
            <Col md={3} sm={6}><div className="border rounded p-3 h-100"><div className="small text-muted">Collected</div><h5 className="mb-0 text-success">{formatMoney(Math.max(Number(snapshot.evidenced_paid_amount || 0), Number(snapshot.paid_amount || 0), Number(snapshot.collected_amount || 0)))}</h5></div></Col>
            <Col md={3} sm={6}><div className="border rounded p-3 h-100"><div className="small text-muted">Balance due</div><h5 className="mb-0 text-warning">{formatMoney(snapshot.due_amount)}</h5></div></Col>
          </Row>

          {billRows.length > 0 && (
            <details className="border rounded bg-light p-3 mb-4">
              <summary className="fw-bold text-primary" style={{ cursor: 'pointer' }}>
                Show bills behind the collected total ({snapshot.bill_count || billRows.length} bills · {formatMoney(billTotal)})
              </summary>
              <Alert color="warning" className="mt-3 mb-2">
                The collected total is the sum of active fee lines recorded on these bills. It is payment evidence—not proof of the approved fee amount.
              </Alert>
              <div className="table-responsive" style={{ maxHeight: '420px' }}>
                <Table bordered hover size="sm" className="mb-0 bg-white">
                  <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr><th>Bill number</th><th>Fee type collected</th><th>Date</th><th>Student ID</th><th>Student name</th><th>Transaction</th><th>Method</th><th className="text-end">Fee amount</th></tr>
                  </thead>
                  <tbody>{billRows.map((bill) => (
                    <tr key={bill.transaction_row_id || `${bill.bill_number}:${bill.student_id}`}>
                      <td>{bill.bill_number ? <Button color="link" className="p-0 fw-bold bill-number-link" onClick={() => this.openBillDetails(bill)}>{bill.bill_number}</Button> : '—'}</td>
                      <td><strong>{bill.legacy_fee_name || fee.legacy_fee_name || '—'}</strong></td>
                      <td>{bill.date || '—'}</td>
                      <td><code>{bill.student_display_id || bill.student_id || '—'}</code></td>
                      <td>{bill.student_name || '—'}</td>
                      <td>{bill.transaction_number || bill.transaction_id || '—'}</td>
                      <td>{getPaymentType(bill.payment_method) || '—'}</td>
                      <td className="text-end fw-bold">{formatMoney(bill.amount)}</td>
                    </tr>
                  ))}</tbody>
                  <tfoot className="table-light"><tr><th colSpan="7" className="text-end">Total from active bill lines</th><th className="text-end">{formatMoney(billTotal)}</th></tr></tfoot>
                </Table>
              </div>
            </details>
          )}

          <Label className="form-label fw-bold">What should “{fee.legacy_fee_name}” become?</Label>
          <Select
            {...this.selectMenuProps()}
            value={this.mappingChoice(editor, fee)}
            options={this.targetOptions(fee)}
            isDisabled={locked || busy}
            placeholder="Choose the matching new fee"
            onChange={(option) => this.setMappingChoice(fee, option)}
          />
          <div className="small text-muted mt-2">
            Choose <strong>divide equally</strong> when the old fee was one combined amount. Choose a specific part, such as <strong>Semester 1 only</strong>, when the old fee already represented that part.
          </div>
          <div className="small text-primary mt-1">
            <i className="mdi mdi-layers-triple-outline me-1" />If other old fees use the same new fee part, they will be stacked and added together. Preview shows the combined result before approval.
          </div>
          <div className="small text-muted mt-1">
            TERM and STAT are managed automatically from PHP Set Fee and are not available as manual targets.
          </div>
          {fee.suggested_canonical_code && !mapping && !editor.target_canonical_code && (
            <div className="small text-muted mt-2">
              Suggested from the old name: <strong>{fee.suggested_canonical_code}</strong>. Please confirm it before saving.
            </div>
          )}

          <div className="mt-3">
            <Label className="form-label">Note <span className="text-muted">(optional)</span></Label>
            <Input type="text" maxLength={500} value={editor.review_notes || ''} disabled={locked || busy} placeholder="Example: Confirmed with accounts team" onChange={(event) => this.updateEditor(this.feeKey(fee), { review_notes: event.target.value })} />
          </div>

          {this.renderPreview(editor.preview)}

          <Alert color="light" className="border mt-3 mb-0">
            <i className="mdi mdi-lock-outline me-1" />This only records how the old fee should be displayed. Existing payments, bills, and receipts will not be changed.
          </Alert>

          <details className="mt-3">
            <summary className="text-muted" style={{ cursor: 'pointer' }}>Show migration details</summary>
            <div className="small text-muted mt-2">
              <div>Old parent fee: {fee.legacy_parent_fee_name || 'Not available'}</div>
              <div>Database stored year: <strong>{fee.source_academic_year || `Year ${fee.source_study_year || '?'}`}</strong></div>
              <div>Administrator target year: <strong>{this.state.inventory?.selection?.target_year_label || this.state.selectedAcademic?.label}</strong></div>
              <div>Stable record ID: <code>{fee.legacy_sub_fee_id}</code></div>
              {fee.source_fee_id !== null && fee.source_fee_id !== undefined && <div>Old-system ID: <code>{fee.source_fee_id}</code></div>}
              <div>Fee rows: {snapshot.student_fee_records || 0} · Payment rows: {snapshot.payment_rows || 0} · Collected in payment records: {formatMoney(snapshot.collected_amount)}</div>
              {mapping?.archive_checksum && <div>Protected archive check: <code>{mapping.archive_checksum.slice(0, 12)}…</code></div>}
              <div className="mt-1">{(fee.lineage_evidence || []).map((item) => <Badge key={item} color="light" className="text-dark me-1">{item}</Badge>)}</div>
            </div>
          </details>

          <div className="d-flex flex-wrap gap-2 mt-4 pt-3 border-top">
            {!mapping?.rule_locked && <Button color="secondary" outline disabled={busy} onClick={() => this.previewMapping(fee)}>{busy && this.state.workingKey.startsWith('preview') ? <Spinner size="sm" className="me-1" /> : null}{locked ? 'Recheck combined amounts' : 'Preview combined result'}</Button>}
            {!locked && <Button color="primary" disabled={busy} onClick={() => this.saveDraft(fee)}>{busy && this.state.workingKey.startsWith('draft') ? <Spinner size="sm" className="me-1" /> : null}Save as draft</Button>}
            {mapping?.mapping_status === 'draft' && <Button color="info" disabled={busy} onClick={() => this.runLifecycle(fee, 'approve')}>Approve this mapping</Button>}
            {mapping?.mapping_status === 'approved' && <Button color="warning" outline disabled={busy} onClick={() => this.runLifecycle(fee, 'reopen')}>Make changes</Button>}
            {mapping?.mapping_status === 'approved' && <Button color="success" disabled={busy} onClick={() => this.runLifecycle(fee, 'apply')}><i className="mdi mdi-shield-lock-outline me-1" />Save permanently</Button>}
            {mapping?.rule_locked ? (
              <span className="small text-success align-self-center"><i className="mdi mdi-check-circle me-1" />System rule applied. No administrator review is required; old financial records remain unchanged.</span>
            ) : mapping?.mapping_status === 'applied' && <span className="small text-success align-self-center"><i className="mdi mdi-check-circle me-1" />Saved permanently. Old financial records and the protected mapping copy are preserved.</span>}
          </div>
        </ModalBody>
      </Modal>
      {this.renderBillDetailsModal()}
      </>
    );
  };

  renderCurrentStacks = () => {
    const stacks = (this.state.inventory?.target_stacks || []).filter((group) => group.source_count > 1);
    if (!stacks.length) return null;
    return (
      <Card>
        <CardBody>
          <h5 className="mb-1">Current stacked targets</h5>
          <p className="text-muted">These new fee parts already combine more than one old fee for {this.state.inventory?.selection?.target_year_label}.</p>
          <Table responsive bordered size="sm" className="mb-0">
            <thead className="table-light"><tr><th>New fee part</th><th>Old fees combined</th><th className="text-end">Students</th><th className="text-end">Combined fee</th><th className="text-end">Collected</th><th className="text-end">Due</th></tr></thead>
            <tbody>{stacks.map((group) => (
              <tr key={group.stack_key}>
                <td><strong>{group.canonical_code}</strong> — {group.installment_label}</td>
                <td>{(group.legacy_sources || []).map((source) => source.legacy_fee_name).join(' + ')}</td>
                <td className="text-end">{group.students_affected || 0}</td>
                <td className="text-end">{formatMoney(group.student_actual_amount)}</td>
                <td className="text-end text-success">{formatMoney(group.student_collected_amount)}</td>
                <td className="text-end text-warning">{formatMoney(group.student_due_amount)}</td>
              </tr>
            ))}</tbody>
          </Table>
        </CardBody>
      </Card>
    );
  };

  render() {
    const { inventory } = this.state;
    const visibleFees = (inventory?.legacy_fees || []).filter((fee) => {
      if (!this.state.showCompleted && fee.mapping?.mapping_status === 'applied') return false;
      const query = this.state.search.trim().toLowerCase();
      if (!query) return true;
      return [fee.legacy_fee_name, fee.legacy_parent_fee_name, fee.legacy_sub_fee_id, fee.source_fee_id, fee.source_academic_year]
        .some((value) => String(value ?? '').toLowerCase().includes(query));
    });
    const completedForTarget = (inventory?.legacy_fees || []).filter((fee) => fee.mapping?.mapping_status === 'applied').length;
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Historical Fee Mapping" breadcrumbItems={this.state.breadcrumbItems} />
          <Card>
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <h4 className="mb-1">Complete Historical Migration Review</h4>
                  <div className="text-muted">Work through old fee names, student identities, exceptional records, and the final readiness check.</div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {[
                    ['mapping', '1. Map old fees'],
                    ['identity', '2. Match students'],
                    ['exception', '3. Resolve exceptions'],
                    ['summary', '4. Final check'],
                  ].map(([value, label]) => (
                    <Button key={value} color={this.state.workspace === value ? 'primary' : 'secondary'} outline={this.state.workspace !== value} onClick={() => this.setState({ workspace: value })}>{label}</Button>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
          {this.state.workspace === 'mapping' ? <>
          <Card>
            <CardBody>
              <h4 className="mb-2">Match Old Fees to New Fee Names</h4>
              <p className="text-muted mb-4">Choose the course type, branch, batch, and year where you want to assign old fees. The list searches every migrated year in that exact branch and batch.</p>
              <Row className="g-3">
                <Col xl={3} md={6} xs={12}>
                  <Label className="fw-bold">1. Course Type <span className="text-danger">*</span></Label>
                  <Select {...this.selectMenuProps()} value={this.state.selectedCourseType} options={COURSE_TYPE_OPTIONS} isLoading={this.state.loadingOptions} onChange={this.handleCourseType} placeholder="Select course type" isClearable />
                </Col>
                <Col xl={3} md={6} xs={12}>
                  <Label className="fw-bold">2. Branch / Course / Class <span className="text-danger">*</span></Label>
                  <Select {...this.selectMenuProps()} value={this.state.selectedBranch} options={this.getCourseBranches()} isLoading={this.state.loadingOptions} isDisabled={!this.state.selectedCourseType} onChange={this.handleBranch} placeholder={this.state.selectedCourseType ? 'Select branch or class' : 'Select course type first'} isClearable />
                  {this.state.selectedCourseType && <small className="text-muted">Active and historical branches classified as {this.state.selectedCourseType.label} are shown.</small>}
                </Col>
                <Col xl={3} md={6} xs={12}>
                  <Label className="fw-bold">3. Cohort / Session <span className="text-danger">*</span></Label>
                  <Select {...this.selectMenuProps()} value={this.state.selectedCalendar} options={this.getCompatibleSessions()} isLoading={this.state.loadingAcademic} isDisabled={!this.state.selectedBranch || this.state.loadingAcademic} onChange={this.handleCalendar} placeholder={this.state.selectedBranch ? 'Select cohort or session' : 'Select branch first'} isClearable />
                  {this.state.selectedBranch && !this.state.loadingAcademic && <small className="text-muted">Only cohorts with records for this branch and the approved {this.state.selectedBranch.academic_year}-year duration are shown.</small>}
                </Col>
                <Col xl={3} md={6} xs={12}>
                  <Label className="fw-bold">4. Assign Fees to Year <span className="text-danger">*</span></Label>
                  <Select {...this.selectMenuProps()} value={this.state.selectedAcademic} options={this.state.academicOptions} getOptionLabel={this.getYearLabel} isLoading={this.state.loadingAcademic} isDisabled={!this.state.selectedCalendar || Number(this.state.selectedBranch?.academic_year) === 1} onChange={this.handleAcademic} placeholder={Number(this.state.selectedBranch?.academic_year) === 1 && this.state.selectedCalendar ? 'Selected automatically' : 'Select target year'} isClearable={Number(this.state.selectedBranch?.academic_year) !== 1} />
                </Col>
              </Row>
            </CardBody>
          </Card>

          {this.state.loadingInventory && <div className="text-center py-5"><Spinner color="primary" /><div className="mt-2 text-muted">Loading old fees…</div></div>}
          {inventory && !this.state.loadingInventory && (
            <>
              {!inventory.has_historical_evidence && <Alert color="info">No old fee evidence was found anywhere in this branch and batch.</Alert>}
              {inventory.has_historical_evidence && !inventory.mapping_allowed && (
                <Alert color="info">No unassigned old-fee evidence is available for this target year. Existing work has been assigned to another year.</Alert>
              )}
              {inventory.mapping_allowed && (
                <>
                  <Alert color="primary" className="border-0"><strong>Target year: {inventory.selection?.target_year_label}</strong> — every fee you map on this screen will belong to this year, even if the database currently shows a different migrated year.</Alert>
                  <Alert color="success" className="border-0"><i className="mdi mdi-shield-check-outline me-1" /><strong>Old records are protected.</strong> This screen only tells the application what each old fee means today.</Alert>
                  {this.renderCurrentStacks()}
                  <Card>
                    <CardBody>
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-2">
                        <div>
                          <h5 className="mb-1">Review progress</h5>
                          <div className="text-muted">{inventory.completion?.applied || 0} of {inventory.completion?.total || 0} fees completed</div>
                        </div>
                        <Badge color={(inventory.completion?.remaining || 0) === 0 ? 'success' : 'warning'} className={(inventory.completion?.remaining || 0) === 0 ? '' : 'text-dark'} pill>
                          {(inventory.completion?.remaining || 0) === 0 ? 'All complete' : `${inventory.completion?.remaining || 0} remaining`}
                        </Badge>
                      </div>
                      <Progress value={inventory.completion?.total ? ((inventory.completion.applied || 0) / inventory.completion.total) * 100 : 0} color="success" />
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <div><h5 className="mb-1">Unassigned fees from the whole batch</h5><div className="text-muted">Review only the fees that belong to {inventory.selection?.target_year_label}. Their database-stored year is shown for reference.</div></div>
                        <div className="d-flex flex-wrap gap-2">
                          {completedForTarget > 0 && <Button color="secondary" outline size="sm" onClick={() => this.setState((state) => ({ showCompleted: !state.showCompleted }))}>{this.state.showCompleted ? 'Hide completed' : `Show completed (${completedForTarget})`}</Button>}
                          <Input style={{ maxWidth: '280px' }} value={this.state.search} onChange={(event) => this.setState({ search: event.target.value })} placeholder="Search fee or stored year" />
                        </div>
                      </div>
                      {visibleFees.length ? (
                        <Table responsive hover className="align-middle mb-0">
                          <thead className="table-light"><tr><th>Old fee evidence</th><th className="text-end">Fee total</th><th className="text-end">Collected</th><th className="text-end">Due</th><th>New name</th><th>Status</th><th /></tr></thead>
                          <tbody>{visibleFees.map(this.renderFeeRow)}</tbody>
                        </Table>
                      ) : <Alert color="secondary" className="mb-0">No unassigned fees match this view. Choose another target year or show completed mappings.</Alert>}
                    </CardBody>
                  </Card>
                </>
              )}
            </>
          )}
          {this.renderReviewModal()}
          </> : (
            <SemanticReviewPanel
              mode={this.state.workspace}
              orgId={this.state.org_id}
              operatorId={this.state.operator_id}
            />
          )}
        </Container>
      </div>
    );
  }
}

export default HistoricalFeeMapping;
